import { TutorialConfig, TutorialStep, TutorialTask } from '../types';

const INKSCAPE_TUTORIAL_PROJECT_ID = '00991104-6e4f-48cf-99b1-9aa630676b12';

const INKSCAPE_TUTORIAL_TASKS: TutorialTask[] = [
  {
    id: 'inkscape-project-tour',
    name: 'Explore the labeling workspace',
    type: 'success',
  },
  {
    id: 'inkscape-obvious-success',
    name: 'Obvious success',
    type: 'success',
    runId: 'f8afb45e-a210-420f-bf53-397558106152',
    description: 'Problem 75550ecf-a5fc-4f8c-8f60-02bd928c48d8',
  },
  {
    id: 'inkscape-obvious-failure',
    name: 'Obvious failure',
    type: 'failure',
    runId: '8d7bbbae-9b45-45ed-a8ca-f33039935b6b',
    description: 'Problem 47f6f1e0-1838-4f79-9b43-c4803738d0b9',
  },
  {
    id: 'inkscape-hidden-success',
    name: 'Success that looks like a fail',
    type: 'success',
    runId: 'e7b80f39-854d-41db-9c62-eef34536acc2',
    description: 'Problem 4a205467-6f3e-4bc0-82d4-efe168cf749b',
  },
  {
    id: 'inkscape-hidden-failure',
    name: 'Fail that looks like success',
    type: 'failure',
    runId: 'dabdecdf-d2cb-4453-b377-c4334bbd8d0b',
    description: 'Problem 89fe324f-6f07-4c52-b3aa-af7fb09b8cda',
  },
  {
    id: 'inkscape-ambiguous-task',
    name: 'Ambiguous task',
    type: 'bad_task',
    runId: 'dd9b97d9-6733-4e3e-8a47-9187c9151601',
    description: 'Problem ed7fdbdb-6bcf-40a4-9133-e9b0df615eb7',
  },
];

const INKSCAPE_TUTORIAL_STEPS: TutorialStep[] = [
  // ============================================================================
  // SHARED PROJECT TOUR FRAMES (frames 1-13)
  // Keep these identical across all tutorials
  // ============================================================================
  {
    id: 'inkscape-project-welcome',
    title: 'Welcome to Inkscape Labeling',
    content:
      'This tutorial gives you a quick tour of the labeling workspace. You can explore every part of the UI without affecting real data.\n\nOnce you claim a labeling project, you can click on the project to be brought to this page.\n\nIf you would like to exit the tutorial, just click "Exit Tutorial" on the top bar. You can always restart the tutorial by clicking "Show Tutorial."',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },
  {
    id: 'inkscape-problem-list',
    title: 'Assigned Problems',
    content:
      'Each card in this list represents a problem. The badge tracks how many runs you have labeled. Feel free to click through the list during the tutorial.',
    targetSelector: '.problem-list',
    action: 'explore',
    position: 'right',
    allowInteraction: true,
    scope: 'project',
  },
  {
    id: 'inkscape-solution-upload',
    title: 'Solution Uploads',
    content:
      'Projects consist of two steps: first, you must upload the solution for the problem, and next, you should label all of the runs for that problem. When uploading your solution, make sure that your uploaded filename matches exactly the filename of the solution requested in the prompt.',
    targetSelector: '.solution-upload-section',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'inkscape-bad-task-button',
    title: 'Mark Ambiguous Problems',
    content:
      'If the prompt is ambiguous or has multiple valid outcomes, mark it as a bad task here. Provide a concise reason so reviewers understand the issue. After marking as ambiguous, all runs will automatically be labeled and you will not have to upload a solution.',
    targetSelector: '.bad-task-btn',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'solution-upload-requirement',
    title: 'Solution Required',
    content:
      'Important: You must upload your solution before you can select and label any runs. This ensures you have solved the problem yourself before evaluating the AI\'s attempts. Notice that the runs below are currently grayed out.',
    targetSelector: '.run-list',
    action: 'next',
    position: 'left',
    scope: 'project',
    maxWidth: 320,
  },
  {
    id: 'solution-upload-select-run',
    title: 'Runs to Review',
    content:
      'Every run shows a model attempt. Now you can select one to open the viewer so you can study exactly how the agent approached the task. Click on any run below to proceed.',
    targetSelector: '.run-list',
    action: 'click',
    position: 'left',
    allowInteraction: true,
    scope: 'project',
    nextButtonLabel: 'Select a run to proceed',
  },
  {
    id: 'inkscape-viewer-overview',
    title: 'Review Each Attempt',
    content:
      'The video displays the AI solving the task. The AI has a computer environment consisting of many applications like Audacity, LibreOffice, Inkscape, Gimp, and Shotcut, and can use any of these applications to solve the task. The AI can also use programming and bash scripting. Anything is fair game as long as the AI solves the task as requested!',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-viewer-timeline',
    title: 'Timeline Controls',
    content:
      'Use the timeline to jump between frames. Speed controls and keyboard shortcuts help you inspect long attempts quickly without skipping critical moments.',
    targetSelector: '.timeline-container',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'inkscape-viewer-command-panel',
    title: 'Command History',
    content:
      'The agent can launch shell commands, scripts, or custom tools while it works. Every command it runs appears here so you can verify exactly what code or terminal actions were executed.',
    targetSelector: '.tool-output-section:first-child',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-viewer-thoughts',
    title: 'Agent Notes',
    content:
      'Read the thought bubble to see why the agent acted. When judging ambiguity, these notes often reveal whether the prompt had multiple valid interpretations.',
    targetSelector: '.thought-process',
    action: 'next',
    position: 'right',
    scope: 'viewer',
  },
  {
    id: 'inkscape-viewer-inspect',
    title: 'Inspect the Attempt',
    content:
      'Watch the video carefully to see every step the AI took. When you need the final output, right-click the video to download the files it produced and compare them with your ground-truth solution.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-viewer-evaluate',
    title: 'Label the Run',
    content:
      'Use this panel to decide whether the run succeeded or failed. If you realize that a problem is ambiguous or has multiple correct solutions, return to the problem workspace, mark it as a bad task, and then move on to the next problem.',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    maxWidth: 360,
    scope: 'viewer',
    offsetY: -50,
  },
  {
    id: 'inkscape-viewer-return',
    title: 'Return to the Problem Workspace',
    content:
      'Click "Back to Problem" when you finish reviewing runs. That returns you to the overview so you can upload a solution or mark the problem ambiguous.',
    targetSelector: '.back-to-problem-btn',
    action: 'click',
    position: 'right',
    scope: 'viewer',
    allowInteraction: true,
  },
  {
    id: 'inkscape-project-recap',
    title: 'Wrap Up the Problem',
    content:
      'From this workspace you can revisit the prompt, upload the required solution file, and finish labeling every run. Ambiguous problems can be waived entirely.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
  },
  {
    id: 'inkscape-example-transition',
    title: 'Run Labeling Examples',
    content:
      'Now let us review real AI runs and decide whether each is a success or a failure. We will walk through several examples together.',
    targetSelector: '.problem-detail',
    action: 'next',
    position: 'bottom',
    scope: 'project',
    nextButtonLabel: 'Next Problem',
  },

  // ============================================================================
  // INKSCAPE-SPECIFIC EXAMPLE STEPS
  // ============================================================================

  // Example 1: Obvious Success
  // Example 1: Obvious Success
  {
    id: 'inkscape-obvious-success-intro',
    title: 'Obvious Success',
    content:
      "Here's an example of an obvious success. If we scroll through the video, we see that the AI successfully intersected the squares with the man's hands to create gloves of the correct color. A clear success.",
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-obvious-success-conclusion',
    title: 'Mark as Success',
    content:
      'Mark this run a success.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 2: Obvious Failure
  {
    id: 'inkscape-obvious-failure-intro',
    title: 'Obvious Failure',
    content:
      "Here's an example of an obvious fail. The AI is asked to warp the text to the shape of the squiggly square box, but if you scroll through the video, it ends up with a total deformed mess. A clear fail.",
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-obvious-failure-conclusion',
    title: 'Mark as Fail',
    content:
      'This is a clear failure.',
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 3: Success That Looks Like a Fail
  {
    id: 'inkscape-hidden-success-intro',
    title: 'Success Without Inkscape',
    content:
      "Let's take a look at a trickier example. If you just scroll through the video, you might be tempted to label the run a fail. After all, the AI doesn't even open Inkscape at all!",
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-hidden-success-code',
    title: 'Inspect the Script',
    content:
      "But, remember that the AI can write code, and we can see here that it wrote a bash script to modify the input file. Let's try downloading the output file to see what the AI did.",
    targetSelector: '.tool-output-wrapper',
    action: 'seek-frame',
    seekFrame: 3,
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'inkscape-hidden-success-conclusion',
    title: 'Label as Success',
    content:
      "If you download the output file, you'll see that the AI matched the solution perfectly. In other words, this run is a success! No need to try understanding what exactly the AI's code does. Just use the output file to determine if it succeeded or not!",
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'success',
    position: 'top',
    scope: 'viewer',
    offsetY: -100,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 4: Fail That Looks Like a Success
  {
    id: 'inkscape-hidden-failure-intro',
    title: 'Looks Right? Double-Check',
    content:
      "Here's an example of the output file exposing very subtle differences between the AI's output vs solution.",
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-hidden-failure-details',
    title: 'Download to Confirm',
    content:
      "If we scroll through the video, it looks like the AI successfully centered the text and changed the color. It used GIMP instead of Inkscape, but this is totally fine in isolation. Remember, we don't care what tool the AI uses as long as the output file is correct.",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'inkscape-hidden-failure-font',
    title: 'Subtle Font Change',
    content:
      "However, if you download the AI's output and compare it to the solution, you'll notice the font in the AI's output is ever so slightly different. This is because opening the svg file in GIMP automatically changed the font!",
    targetSelector: '.tool-output-wrapper',
    action: 'next',
    position: 'top',
    scope: 'viewer',
  },
  {
    id: 'inkscape-hidden-failure-conclusion',
    title: 'Mark as Fail',
    content:
      "Though subtle, this unasked for modification makes the run a clear fail. Remember, the output file is always the source of truth! Don't forget to inspect it to confirm if the AI was successful or not.",
    targetSelector: '.evaluation-buttons',
    action: 'evaluate',
    expectedEvaluation: 'fail',
    position: 'top',
    scope: 'viewer',
    offsetY: -75,
    nextButtonLabel: 'Next Problem',
    allowInteraction: true,
  },

  // Example 5: Ambiguous Task
  {
    id: 'inkscape-ambiguous-intro',
    title: 'Spot Ambiguity Early',
    content:
      'This prompt asks for "dark gray" on the "tip of the pencil" without defining either. That already hints there may be more than one correct outcome.',
    targetSelector: '.screenshot-wrapper',
    action: 'next',
    position: 'left',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-ambiguous-details',
    title: 'Multiple Valid Answers',
    content:
      'Scroll the timeline and read the thought process. Different hex codes could qualify as “dark gray,” and it is unclear whether the border counts as part of the pencil tip. That means there is no single ground-truth output.',
    targetSelector: '.thought-process',
    action: 'next',
    position: 'right',
    scope: 'viewer',
    maxWidth: 360,
  },
  {
    id: 'inkscape-ambiguous-conclusion',
    title: 'Mark as Bad Task',
    content:
      'If a problem has multiple possible answer, go back to the main problem page and mark it a "bad task"',
    targetSelector: '.evaluation-buttons',
    action: 'next',
    position: 'top',
    scope: 'viewer',
    offsetY: -50,
    nextButtonLabel: 'Next Problem',
  },

  // Final Step
  {
    id: 'tutorial-complete',
    title: 'Nice Work!',
    content:
      'You have finished the Inkscape tutorial. You now know how to tour the workspace, inspect outputs, download and compare SVG files, and identify ambiguous tasks. Remember: the AI can use both Inkscape and code together, which is perfectly acceptable. Always download and carefully inspect the output file—even small visual differences count as failures. When a task has multiple valid interpretations or undefined values like "dark gray," mark it as ambiguous to save time. Good luck on real projects!',
    action: 'next',
    position: 'bottom',
    scope: 'viewer',
  },
];

export const inkscapeTutorialConfig: TutorialConfig = {
  software: 'inkscape',
  displayName: 'Inkscape Labeling Tutorial',
  totalTasks: INKSCAPE_TUTORIAL_TASKS.length,
  tasks: INKSCAPE_TUTORIAL_TASKS,
  steps: INKSCAPE_TUTORIAL_STEPS,
  completionKey: 'labelPlatform_inkscape_TutorialCompleted',
  dataSource: 'api',
  projectId: INKSCAPE_TUTORIAL_PROJECT_ID,
};
